import { useMsal } from '@azure/msal-react'
import { notifications } from '@mantine/notifications'
import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { OnboardingWizard } from '../components/onboarding/OnboardingWizard'
import { useSessionInfo } from '../contexts/SessionContext'
import {
  fetchLocationById,
  initializeLocationData,
} from '../data/location.service'
import { OnboardingDevice } from '../models/Onboarding/OnboardingDevice'
import { OnboardingLocation } from '../models/Onboarding/OnboardingLocation'
import { defaultOnboardingState } from '../models/Onboarding/OnboardingState'
import { HostOnboardingSteps, OnboardingWizardPages } from '../utils/constants'
import { getCurrentStepFromLocation } from '../utils/locationUtils'

export const EditLocationPage = () => {
  const { session } = useSessionInfo()
  const { instance } = useMsal()
  const organizationId = session?.organizationId
  const [location, setLocation] = useState<OnboardingLocation>(
    defaultOnboardingState.location,
  )
  const [device, setDevice] = useState<OnboardingDevice | null>(null)
  const [isLoadingLocationInfo, setIsLoadingLocationInfo] = useState(false)
  const [currentStep, setCurrentStep] = useState<HostOnboardingSteps>(
    HostOnboardingSteps.LocationName,
  )
  const { locationId } = useParams<{ locationId: string }>()

  useEffect(() => {
    const getLocation = async (locationId: string | undefined) => {
      if (organizationId == null || isLoadingLocationInfo) return

      setIsLoadingLocationInfo(true)
      try {
        const locationById = await fetchLocationById(locationId ?? '-1')
        setLocation(locationById)
        const currentStep = getCurrentStepFromLocation(locationById)
        setCurrentStep(currentStep)
      } catch (ex: any) {
        console.log(ex)
      } finally {
        setIsLoadingLocationInfo(false)
      }
    }

    const initializeLocation = async (organizationId: number) => {
      setIsLoadingLocationInfo(true)
      try {
        const locationData = await initializeLocationData(
          organizationId,
          instance,
        )
        setLocation(locationData)
      } catch (ex: any) {
        notifications.show({
          title: 'An unknown error occurred',
          message:
            'Unable to get organization info. Refresh page. If you are unable to resolve the issue, please contact us for help',
          autoClose: false,
        })
      } finally {
        setIsLoadingLocationInfo(false)
      }
    }

    if (locationId) {
      getLocation(locationId)
    } else if (organizationId) {
      initializeLocation(organizationId)
    }
  }, [locationId, organizationId, instance])

  return (
    <OnboardingWizard
      location={location}
      setLocation={setLocation}
      device={device}
      setDevice={setDevice}
      isLoadingState={isLoadingLocationInfo}
      pageName={OnboardingWizardPages.ManageLocation}
      currentStep={currentStep}
      setCurrentStep={setCurrentStep}
    />
  )
}
